import { useRef, type PropsWithChildren, useState, CSSProperties } from 'react';
import { useToast } from './useToast';
import styles from './styles.scss';
import cn from 'classnames';
import { AlertContainer } from 'components/AlertNotification';
import { DisplayType } from 'models/AlertNotification';
import { NoSsr } from '@bygghemmait/components';
import { IconCross } from 'components/Shared/Icon/themes/BGH-RD3/iconCross';
import { Transition } from './transition';
import { Delay } from './delay';
import { Button } from 'components/Button';
import { styled } from 'styled-components';

type ProgressBarProps = {
  delay: number;
  isRunning: boolean;
  closeToast: () => void;
};
const ProgressBar = ({ delay, isRunning, closeToast }: ProgressBarProps) => {
  return (
    <div
      role="progressbar"
      aria-label="notification timer"
      className={cn(styles.progress)}
      style={{
        animationDuration: `${delay}ms`,
        animationPlayState: isRunning ? 'running' : 'paused'
      }}
      onAnimationEnd={closeToast}
    />
  );
};

const CloseButton = styled(Button)({
  position: 'absolute',
  top: '0.2em',
  right: '0.2em',
  color: 'var(--alert-border-clr)',
  path: {
    stroke: 'currentcolor'
  }
});

type ToastProps = PropsWithChildren<{
  type: DisplayType;
  delay?: number;
  progressDelay?: number;
  timeout?: number;
  className?: string;
}>;
export function Toast({
  type,
  children,
  delay = 1000,
  timeout = 200,
  progressDelay = 10000,
  className
}: ToastProps) {
  const [isIn, setIsIn] = useState(true);
  const closeToast = () => setIsIn(false);
  const ref = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { playToast, eventHandlers, isRunning } = useToast(
    { closeToast, thresholdPercent: 30 },
    ref,
    wrapperRef
  );

  return (
    <NoSsr defer>
      <Delay delay={delay}>
        <Transition playToast={playToast} isIn={isIn} nodeRef={ref}>
          {state => (
            <div ref={wrapperRef} {...eventHandlers} className={styles.wrapper}>
              <AlertContainer
                ref={ref}
                displayType={type}
                style={
                  {
                    ['--toast-timeout']: `${timeout}ms`
                  } as CSSProperties
                }
                className={cn(styles.toast, className, {
                  [styles.enter]: state === 'entering',
                  [styles.exit]: state === 'exiting'
                })}
              >
                <ProgressBar delay={progressDelay} isRunning={isRunning} closeToast={closeToast} />
                <CloseButton appearance="link" onClick={closeToast}>
                  <IconCross />
                </CloseButton>
                {children}
              </AlertContainer>
            </div>
          )}
        </Transition>
      </Delay>
    </NoSsr>
  );
}
